import { ISendInfo, SendCorrespondenceType } from '@alberta/konexi-shared';

/** Icon shown next to the send information block, per correspondence type. */
const ICON_BY_CORRESPONDENCE_TYPE: Readonly<Record<SendCorrespondenceType, string>> = {
  fax: 'alberta-fax',
  email: 'alberta-mail',
  kim: 'alberta-paperplane-solid',
};

/**
 * Order that decides which entry wins when two of them carry the same timestamp. Real
 * timestamps differ by at least a second, so this only matters for identical values.
 */
const TIE_ORDER: readonly SendCorrespondenceType[] = ['kim', 'email', 'fax'];

/** The send information to display, plus everything the call site needs to render it. */
export interface ILatestSendInfo {
  info: ISendInfo;
  correspondenceType: SendCorrespondenceType;
  /** Name of the `ion-icon` for this correspondence type. */
  icon: string;
  /**
   * Whether `info.sentBy` holds a user id. E-mail entries keep the sender's address there,
   * so the `username` pipe must not be applied to them.
   */
  senderIsUserId: boolean;
}

/**
 * Normalises `sentAt` into a usable `Date`, or `null` when there is none.
 *
 * `sentAt` arrives as a `Date` from a fresh send and as an ISO string after a JSON round-trip
 * through the sync pipeline. Anything unparseable yields `null` — callers must not hand such a
 * value to Angular's `DatePipe`, which throws instead of rendering nothing.
 *
 * @example
 * toSendDate(new Date(2026, 5, 29));      // → Date 2026-06-29
 * toSendDate('2026-06-29T08:32:00.000Z'); // → Date 2026-06-29
 * toSendDate('not a date');               // → null
 * toSendDate(null);                        // → null
 */
export function toSendDate(sentAt: ISendInfo['sentAt']): Date | null {
  if (!sentAt) {
    return null;
  }
  const date = sentAt instanceof Date ? sentAt : new Date(sentAt);
  return Number.isNaN(date.getTime()) ? null : date;
}

/**
 * Comparable timestamp of an entry. Entries without a usable timestamp sort last but stay
 * eligible, so a lone undated entry is still shown.
 */
function toMillis(sentAt: ISendInfo['sentAt']): number {
  return toSendDate(sentAt)?.getTime() ?? Number.NEGATIVE_INFINITY;
}

/**
 * Picks the most recent send information across all correspondence types.
 *
 * A document or attachment shows exactly one entry: the latest dispatch, whichever way it went.
 * Returns `undefined` when nothing was ever sent.
 *
 * @example
 * // An attachment faxed on the 1st and sent via KIM on the 2nd:
 * latestSendInfo({ fax: faxedOnThe1st, kim: sentViaKimOnThe2nd });
 * // → { info: sentViaKimOnThe2nd, correspondenceType: 'kim',
 * //     icon: 'alberta-paperplane-solid', senderIsUserId: true }
 *
 * @example
 * latestSendInfo({}); // → undefined, nothing was ever sent
 */
export function latestSendInfo(
  candidates: Partial<Record<SendCorrespondenceType, ISendInfo | null | undefined>>
): ILatestSendInfo | undefined {
  let best: { type: SendCorrespondenceType; info: ISendInfo; millis: number } | undefined;

  for (const type of TIE_ORDER) {
    const info = candidates[type];
    if (!info) {
      continue;
    }
    const millis = toMillis(info.sentAt);
    if (!best || millis > best.millis) {
      best = { type, info, millis };
    }
  }

  if (!best) {
    return undefined;
  }

  return {
    info: best.info,
    correspondenceType: best.type,
    icon: ICON_BY_CORRESPONDENCE_TYPE[best.type],
    senderIsUserId: best.type !== 'email',
  };
}
